import { pruebaTerminadaSinPlan, sumarMeses, type TenantEnPrueba } from './finPrueba';

/**
 * Retención de datos de quien NO paga (decisión de Pol, 16 oct 2026).
 *
 * Los datos de una prueba que terminó sin elegir plan se guardan 12 meses
 * desde que terminó la prueba (o los 7 días extra, si los tuvo). Un mes
 * antes (a los 11) se avisa por correo; a los 12, la cuenta se marca para
 * borrarse por el mismo camino que «Borrar mi cuenta» (`tenant.deletion`
 * con motivo `retencion`) y la tarea `tenant-deletion` la borra cuando el
 * interruptor `TENANT_DELETION_ENABLED` esté encendido.
 *
 * Solo pruebas normales que nunca pagaron. Fuera: quien pagó alguna vez y
 * canceló (otra regla, sin decidir), cortesía, cuentas internas (incluida la
 * de la revisión de Apple) y Founder pendientes. La demo la deja fuera la
 * tarea de la API (`isDemo`); la web de la demo sí enseña la fecha.
 *
 * Reglas puras: las usan la API (tarea programada, /admin) y la web (Panel).
 */

/** Meses que se guardan los datos desde el fin de la prueba. */
export const MESES_RETENCION = 12;
/** Meses desde el fin de la prueba en que sale el aviso (uno antes del borrado). */
export const MESES_AVISO_RETENCION = 11;
/**
 * Días que pasan como mínimo entre el aviso y el borrado. Si el aviso salió
 * tarde (correos apagados, un fallo), el borrado se corre para que nadie
 * pierda sus datos sin haber tenido un mes para guardarlos. 28 (el mes más
 * corto): un aviso que sale a tiempo nunca mueve la fecha.
 */
export const DIAS_MINIMOS_TRAS_AVISO = 28;

const DIA = 86_400_000;

/** Lo mínimo del tenant para la regla. */
export interface TenantParaRetencion extends TenantEnPrueba {
  tenantId?: string;
  stripeSubscriptionId?: string;
  commercialEvents?: Array<{ type: string }>;
  trialExtension?: { endsAt: string };
}

/** Las fechas de la retención (ISO completas). */
export interface RetencionCuenta {
  /** Cuándo terminó la prueba (o la extensión): desde ahí está sin plan. */
  sinPlanDesde: string;
  /** Fin + 11 meses: sale el correo de aviso. */
  avisoEl: string;
  /** Fin + 12 meses: la cuenta se marca para borrar. */
  borradoEl: string;
}

/** Suma meses a un ISO conservando la hora (31 ene + 1 mes = 28 feb). */
function sumarMesesIso(iso: string, meses: number): string {
  const d = new Date(iso);
  const completo = d.toISOString();
  return `${sumarMeses(completo.slice(0, 10), meses)}${completo.slice(10)}`;
}

/** El fin de la prueba que cuenta: el más tardío entre `trialEndsAt` y la extensión. */
export function finDePruebaEfectivo(tenant: Pick<TenantParaRetencion, 'trialEndsAt' | 'trialExtension'> | null | undefined): string | undefined {
  const fechas = [tenant?.trialEndsAt, tenant?.trialExtension?.endsAt]
    .map((f) => Date.parse(f ?? ''))
    .filter((n) => Number.isFinite(n));
  if (!fechas.length) return undefined;
  return new Date(Math.max(...fechas)).toISOString();
}

/** ¿Es una prueba normal que nunca pagó? (Sin mirar si ya terminó.) */
export function pruebaSinPagarNunca(tenant: TenantParaRetencion | null | undefined): boolean {
  if (!tenant || tenant.plan !== 'trial' || tenant.subscriptionStatus !== 'trialing') return false;
  if (tenant.firstPaidAt || tenant.stripeSubscriptionId || tenant.internal || tenant.founderPending) return false;
  if (tenant.commercialEvents?.some((e) => e.type === 'alta')) return false;
  return true;
}

/**
 * Las fechas de la retención, o `null` si no le aplica: sigue en prueba,
 * pagó alguna vez, cortesía, interna o Founder pendiente.
 */
export function retencionDe(tenant: TenantParaRetencion | null | undefined, ahora: number = Date.now()): RetencionCuenta | null {
  if (!pruebaSinPagarNunca(tenant)) return null;
  const fin = finDePruebaEfectivo(tenant);
  if (!fin || Date.parse(fin) > ahora) return null;
  // Misma puerta que el cerrojo «ve todo, no suma»: si la app no la trata como sin plan, aquí tampoco.
  if (!pruebaTerminadaSinPlan({ ...tenant!, trialEndsAt: fin }, ahora)) return null;
  return {
    sinPlanDesde: fin,
    avisoEl: sumarMesesIso(fin, MESES_AVISO_RETENCION),
    borradoEl: sumarMesesIso(fin, MESES_RETENCION),
  };
}

/**
 * En qué punto está:
 * - `guardando`: sin plan, menos de 11 meses. Se guardan sus datos.
 * - `aviso`: desde los 11 meses: toca el correo de aviso (una vez).
 * - `borrar`: desde los 12 meses: toca marcar la cuenta para borrar.
 */
export type FaseRetencion = 'guardando' | 'aviso' | 'borrar';

export function faseRetencion(tenant: TenantParaRetencion | null | undefined, ahora: number = Date.now()): FaseRetencion | null {
  const r = retencionDe(tenant, ahora);
  if (!r) return null;
  // Por días (UTC), no por horas: la tarea corre una vez al día y el aviso sale el día que toca.
  const hoy = new Date(ahora).toISOString().slice(0, 10);
  if (hoy >= r.borradoEl.slice(0, 10)) return 'borrar';
  if (hoy >= r.avisoEl.slice(0, 10)) return 'aviso';
  return 'guardando';
}

/**
 * La fecha con la que se marca la cuenta: la del borrado (fin + 12 meses),
 * sin los 14 días de «Borrar mi cuenta» (el aviso ya le dio un mes y le dijo
 * ESA fecha), pero nunca antes de `DIAS_MINIMOS_TRAS_AVISO` desde el aviso.
 */
export function fechaDeBorradoPorRetencion(borradoEl: string, avisoEnviadoEn: string): string {
  const enviado = Date.parse(avisoEnviadoEn.slice(0, 10));
  if (!Number.isFinite(enviado)) return borradoEl;
  const minimo = new Date(enviado + DIAS_MINIMOS_TRAS_AVISO * DIA).toISOString().slice(0, 10);
  if (minimo <= borradoEl.slice(0, 10)) return borradoEl;
  return `${minimo}${new Date(borradoEl).toISOString().slice(10)}`;
}
